import { Sparkles } from "lucide-react";

/**
 * Deterministic Readiness Score — computed in-browser from stored report
 * data. NEVER calls AI.
 *
 * Heuristic (0–100 scale, weighted toward "actionable preparation"):
 *  - 25 base points just for having a structured report.
 *  - up to 25 points for high-priority items identified (capped, 5/issue, 25 max).
 *  - up to 20 points for specialist follow-ups (4/spec, 20 max).
 *  - up to 15 points for combined seller + contractor questions count (1/each, 15 max).
 *  - up to 10 points if a deal_risk_snapshot is present (Premium).
 *  - up to 5 points if a `top_5_priorities` array is populated.
 *
 * The result is intentionally calm and bounded — not gimmicky. Renders only
 * for Premium tier or when score signals are present.
 */
function computeScore(data) {
    if (!data || typeof data !== "object") return null;
    let s = 25;
    const issues = Array.isArray(data.issues) ? data.issues : [];
    const highCount = issues.filter((i) => i.severity === "high").length;
    s += Math.min(25, highCount * 5);
    const specs = Array.isArray(data.specialist_recommendations) ? data.specialist_recommendations.length : 0;
    s += Math.min(20, specs * 4);
    const cs = data.closing_strategy || {};
    const sellerQ = Array.isArray(cs.questions_for_seller) ? cs.questions_for_seller.length : 0;
    const contractorQ = Array.isArray(cs.questions_for_contractor) ? cs.questions_for_contractor.length : 0;
    s += Math.min(15, sellerQ + contractorQ);
    if (cs.deal_risk_snapshot && typeof cs.deal_risk_snapshot === "object") s += 10;
    if (Array.isArray(data.top_5_priorities) && data.top_5_priorities.length > 0) s += 5;
    return Math.max(0, Math.min(100, s));
}

export default function ReadinessScore({ data, isPremium = false }) {
    const score = computeScore(data);
    // Only show for Premium reports OR when there's substantive premium-like signal.
    const hasPremiumSignal = !!data?.closing_strategy;
    if (score === null || (!isPremium && !hasPremiumSignal)) return null;

    const tone =
        score >= 75
            ? { bg: "bg-bl-teal-tint", text: "text-bl-primary", label: "Strong" }
            : score >= 55
            ? { bg: "bg-amber-50", text: "text-amber-700", label: "Building" }
            : { bg: "bg-rose-50", text: "text-rose-700", label: "Early" };

    return (
        <section
            className="bl-card p-6 md:p-7 flex items-start gap-5"
            data-testid="report-readiness-score"
        >
            <div className="flex-shrink-0">
                <div
                    className={
                        "relative w-24 h-24 rounded-full flex items-center justify-center " +
                        tone.bg
                    }
                >
                    <span className={"font-display font-semibold text-3xl tnum " + tone.text}>
                        {score}
                    </span>
                </div>
            </div>
            <div className="min-w-0 flex-1">
                <div className="bl-eyebrow">
                    <Sparkles className="w-3.5 h-3.5" /> Negotiation readiness
                </div>
                <h3 className="mt-2 font-display font-semibold text-bl text-lg">
                    {tone.label} preparation · {score}/100
                </h3>
                <p className="mt-2 text-sm text-bl-muted leading-relaxed">
                    This score reflects how much actionable negotiation
                    preparation has been organized from your inspection
                    findings — priority items, specialist follow-ups, seller
                    and contractor questions, and (for Premium) the strategic
                    risk snapshot. It is not a verdict on the property; it's a
                    readiness check on you.
                </p>
            </div>
        </section>
    );
}
